import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { useOrgOnboardingGate } from 'src/features/organization/hooks/useOrgOnboardingGate';
import { useProductDrafts } from 'src/features/product/product-list/hooks/useProductDrafts';
import { useProducts } from 'src/features/product/product-list/hooks/useProducts';
import ArchivedTabContent from 'src/features/product/product-list/ui/ArchivedTabContent';
import SourcingTabContent from 'src/features/product/product-list/ui/SourcingTabContent';
import resolveProductsTab from 'src/features/product/product-list/utils/resolveProductsTab';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { usePageTitle } from 'src/shared/hooks';

import { Tab } from 'src/shared/components/ui';
import { ButtonV2, TabsV2, Typography } from 'src/shared/components/ui';

const ENTERPRISE_CLASSIFICATIONS = new Set(['enterprise']);

const ProductsPage = () => {
  usePageTitle('Products | Cavela');
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { isLoading, isFetching, data: products, isSuccess } = useProducts();
  const {
    isLoading: isLoadingDrafts,
    isFetching: isFetchingDrafts,
    data: drafts,
    isSuccess: isSuccessDrafts
  } = useProductDrafts();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeDrafts = useMemo(() => {
    return drafts?.filter((draft) => !draft.archived);
  }, [drafts]);

  const isEmpty =
    !isFetching &&
    !isFetchingDrafts &&
    products?.length === 0 &&
    isSuccess &&
    activeDrafts?.length === 0 &&
    isSuccessDrafts;

  const { data: onboardingGate, isLoading: isLoadingGate } =
    useOrgOnboardingGate(isEmpty);

  const classification = onboardingGate?.classification ?? null;

  const shouldGoToDiscovery =
    isEmpty &&
    !isLoadingGate &&
    !!onboardingGate &&
    ENTERPRISE_CLASSIFICATIONS.has(classification ?? '') &&
    !onboardingGate.has_discovery_booking;

  const shouldGoToBuilder =
    isEmpty &&
    !isLoadingGate &&
    !!onboardingGate &&
    !ENTERPRISE_CLASSIFICATIONS.has(classification ?? '');

  useEffect(() => {
    if (shouldGoToDiscovery) {
      navigate(ROUTE_PATHS.ONBOARDING_DISCOVERY, { replace: true });
    } else if (shouldGoToBuilder) {
      navigate(ROUTE_PATHS.BUILD_ANYTHING, { replace: true });
    }
  }, [shouldGoToDiscovery, shouldGoToBuilder, navigate]);

  const activeTab = useMemo(() => {
    return resolveProductsTab(searchParams.get('tab'));
  }, [searchParams]);

  const notArchivedProducts = useMemo(() => {
    return products?.filter((product) => !product.archived);
  }, [products]);

  const archivedProducts = useMemo(() => {
    return products?.filter((product) => product.archived);
  }, [products]);

  if (isEmpty) {
    if (isLoadingGate || shouldGoToDiscovery || shouldGoToBuilder) {
      return null;
    }

    return (
      <div className="mx-auto flex h-dvh max-w-[1200px] flex-col gap-6 overflow-hidden px-6 pb-10 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
          <Typography variant="h1" weight="bold" className="flex items-center">
            Welcome to Cavela, {user?.username}
            <motion.div
              initial={{ rotate: 0 }}
              animate={{
                rotate: [0, 15, -15, 15, -15, 15, 0]
              }}
              transition={{
                duration: 3,
                ease: 'easeInOut',
                delay: 0.5
              }}
            >
              👋
            </motion.div>
            !
          </Typography>
          <ButtonV2
            size="lg"
            onPress={() => navigate(ROUTE_PATHS.BUILD_ANYTHING)}
          >
            Create a new product
          </ButtonV2>
        </div>

        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl"
          style={{
            backgroundImage: 'url(/backgrounds/cavela-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'top'
          }}
        >
          <div className="flex size-full flex-col items-center justify-center gap-8 bg-black bg-opacity-20 px-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ease: 'easeInOut', duration: 1 }}
              className="text-center"
            >
              <Typography variant="h2" weight="bold" className="text-white">
                Let the virtual agent guide you
                <br />
                through your sourcing process
              </Typography>
            </motion.div>

            <motion.img
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ease: 'easeInOut', duration: 1, delay: 0.5 }}
              draggable="false"
              className="h-auto max-h-[48%] w-[42%] object-contain"
              alt="ipad"
              src="/backgrounds/no-products-bg.png"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="products-display" className="h-full py-4 flex flex-col">
      <div
        aria-label="test"
        className="flex items-center justify-between mb-2 px-6"
      >
        <Typography as="h1" variant="h2" weight="bold">
          Products
        </Typography>
      </div>

      <div className="px-3">
        <TabsV2
          selectedKey={activeTab}
          onSelectionChange={(key) => setSearchParams({ tab: `${key}` })}
        >
          <Tab key="sourcing" title="Sourcing" />
          <Tab key="archived" title="Archived" />
        </TabsV2>
      </div>
      <div className="w-full h-[1px] bg-gray-4 relative top-[-4px]" />
      <div className="py-4 px-6 flex-grow">
        {activeTab === 'archived' ? (
          <ArchivedTabContent
            isLoading={isLoading}
            products={archivedProducts}
            isSuccess={isSuccess}
          />
        ) : (
          <SourcingTabContent
            isLoading={isLoading}
            products={notArchivedProducts}
            isSuccess={isSuccess}
            isLoadingIncomplete={isLoadingDrafts}
            incompleteProducts={activeDrafts}
            isSuccessIncomplete={isSuccessDrafts}
          />
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
